import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supportAPI } from '../services/api';

const ADMIN_EMAIL = 'tambe.raju18@gmail.com';

const STATUS_COLORS = {
  OPEN:        { bg: '#fef3c7', color: '#92400e', label: 'Open' },
  IN_PROGRESS: { bg: '#dbeafe', color: '#1e40af', label: 'In Progress' },
  RESOLVED:    { bg: '#d1fae5', color: '#065f46', label: 'Resolved' },
  CLOSED:      { bg: '#f3f4f6', color: '#6b7280', label: 'Closed' },
};

const CATEGORY_ICONS = {
  'Payment Issue':         '💳',
  'Login / Account':       '🔐',
  'Feature Not Working':   '⚠️',
  'Bug Report':            '🐛',
  'Feature Request':       '✨',
  'Billing & Subscription':'💰',
  'Other':                 '📌',
};

export default function IssuesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [tickets,       setTickets]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [filterStatus,  setFilterStatus]  = useState('ALL');
  const [expandedId,    setExpandedId]    = useState(null);
  const [updatingId,    setUpdatingId]    = useState(null);

  /* Redirect non-admins immediately */
  useEffect(() => {
    if (user && user.email !== ADMIN_EMAIL) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user || user.email !== ADMIN_EMAIL) return;
    fetchTickets();
  }, [user]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await supportAPI.getAll();
      setTickets(res.data);
    } catch {
      // Access denied or error
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    setUpdatingId(ticketId);
    try {
      const res = await supportAPI.updateStatus(ticketId, newStatus);
      setTickets(ts => ts.map(t => t.id === ticketId ? res.data : t));
    } catch {
      alert('Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const filtered = filterStatus === 'ALL'
    ? tickets
    : tickets.filter(t => t.status === filterStatus);

  const counts = tickets.reduce((acc, t) => {
    acc[t.status] = (acc[t.status] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>Loading tickets...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.35rem' }}>
          <span style={{ fontSize: '1.5rem' }}>🎫</span>
          <h1 style={{ margin: 0, fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.6rem', color: '#0f1117' }}>
            Support Issues
          </h1>
        </div>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
          Admin view · {tickets.length} total ticket{tickets.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        {Object.entries(STATUS_COLORS).map(([key, { bg, color, label }]) => (
          <div key={key} style={{ background: bg, borderRadius: '10px', padding: '0.6rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
            <span style={{ fontSize: '1.3rem', fontWeight: 800, color }}>{counts[key] || 0}</span>
            <span style={{ fontSize: '0.72rem', fontWeight: 600, color }}>{label}</span>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        {['ALL', 'OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'].map(s => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            style={{
              padding: '0.4rem 0.9rem', borderRadius: '8px', border: 'none',
              fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer',
              background: filterStatus === s ? '#0f1117' : '#f3f4f6',
              color:      filterStatus === s ? 'white'   : '#6b7280',
              transition: 'all 0.15s',
            }}
          >
            {s === 'ALL' ? 'All' : STATUS_COLORS[s]?.label || s}
            {s !== 'ALL' && counts[s] ? ` (${counts[s]})` : ''}
          </button>
        ))}
      </div>

      {/* Ticket list */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af', fontSize: '0.9rem' }}>
          No tickets found.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filtered.map(ticket => {
            const statusInfo = STATUS_COLORS[ticket.status] || STATUS_COLORS.OPEN;
            const isExpanded = expandedId === ticket.id;

            return (
              <div key={ticket.id}
                style={{
                  background: 'white', borderRadius: '14px',
                  border: '1px solid #e5e7eb',
                  boxShadow: isExpanded ? '0 4px 20px rgba(0,0,0,0.08)' : '0 1px 4px rgba(0,0,0,0.04)',
                  transition: 'box-shadow 0.15s',
                  overflow: 'hidden',
                }}>

                {/* Ticket header */}
                <div
                  onClick={() => setExpandedId(isExpanded ? null : ticket.id)}
                  style={{ padding: '1rem 1.25rem', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '1rem' }}
                >
                  <span style={{ fontSize: '1.4rem', flexShrink: 0, marginTop: '2px' }}>
                    {CATEGORY_ICONS[ticket.category] || '📌'}
                  </span>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f1117' }}>
                        {ticket.subject}
                      </span>
                      <span style={{
                        background: statusInfo.bg, color: statusInfo.color,
                        borderRadius: '6px', padding: '0.15rem 0.5rem',
                        fontSize: '0.7rem', fontWeight: 700,
                      }}>
                        {statusInfo.label}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#6b7280', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      <span>👤 {ticket.name || 'Unknown'}</span>
                      <span>📧 {ticket.email || '—'}</span>
                      <span>🏷️ {ticket.category}</span>
                      <span>🕒 {ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</span>
                    </div>
                  </div>

                  <span style={{ fontSize: '0.85rem', color: '#9ca3af', flexShrink: 0, marginTop: '2px' }}>
                    {isExpanded ? '▲' : '▼'}
                  </span>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div style={{ padding: '0 1.25rem 1.25rem', borderTop: '1px solid #f3f4f6' }}>
                    <div style={{ paddingTop: '1rem' }}>

                      {/* Description */}
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#9ca3af', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Description
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap', background: '#f9fafb', borderRadius: '8px', padding: '0.75rem 1rem' }}>
                          {ticket.description || '—'}
                        </div>
                      </div>

                      {/* Screenshot */}
                      {ticket.imageBase64 && (
                        <div style={{ marginBottom: '1rem' }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#9ca3af', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Screenshot
                          </div>
                          <img
                            src={ticket.imageBase64}
                            alt="screenshot"
                            style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #e5e7eb', display: 'block' }}
                          />
                        </div>
                      )}

                      {/* Status update */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>Update status:</span>
                        {Object.entries(STATUS_COLORS).map(([key, { label, bg, color }]) => (
                          <button
                            key={key}
                            disabled={ticket.status === key || updatingId === ticket.id}
                            onClick={() => handleStatusChange(ticket.id, key)}
                            style={{
                              padding: '0.3rem 0.75rem', borderRadius: '7px', border: 'none',
                              fontWeight: 600, fontSize: '0.75rem', cursor: ticket.status === key ? 'default' : 'pointer',
                              background: ticket.status === key ? bg : '#f3f4f6',
                              color:      ticket.status === key ? color : '#6b7280',
                              opacity: updatingId === ticket.id ? 0.6 : 1,
                              transition: 'all 0.15s',
                            }}
                          >
                            {updatingId === ticket.id && ticket.status !== key ? '...' : label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
